import type {
  CollectionColumnDef,
  CollectionFilterDef,
  CollectionFilterState,
  CollectionFilterValue,
  CollectionOperator,
  CollectionOperatorValue,
  CollectionSavedView,
  CollectionSort,
  CollectionState,
} from './types'

const FILTER_QUERY_KEY = 'filters'
const SORT_QUERY_KEY = 'sort'
const PRESET_QUERY_KEY = 'preset'

export interface CollectionUrlStateOptions {
  search?: string
  urlKey?: string
  urlFilterFormat?: 'json' | 'flat'
}

export interface CollectionSearchOptions extends CollectionUrlStateOptions {
  defaultPageSize?: number
  presetId?: string | null
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

export function defaultRowKey<RowT>(row: RowT, index: number): string {
  if (isRecord(row)) {
    const id = row.id
    if (typeof id === 'string' || typeof id === 'number') return String(id)
  }
  return `row-${index}`
}

export function readField<RowT>(row: RowT, id: string): unknown {
  return isRecord(row) ? row[id] : undefined
}

export function isEmptyFilterValue(value: CollectionFilterValue | undefined): boolean {
  if (value === undefined || value === null) return true
  if (typeof value === 'string') return value.trim() === ''
  if (Array.isArray(value)) return value.length === 0
  if (typeof value === 'boolean') return false
  if (isOperatorValue(value)) {
    if (value.op === 'empty' || value.op === 'notEmpty') return false
    return isEmptyFilterValue(value.value)
  }
  if (isDateRangeValue(value)) return !value.from && !value.to
  if (isNumberRangeValue(value)) return value.min === undefined && value.max === undefined
  return true
}

export function toText(value: unknown): string {
  if (value === undefined || value === null) return ''
  if (value instanceof Date) return value.toISOString()
  return String(value)
}

function toNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

function toBoolean(value: unknown): boolean | null {
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value === 1 ? true : value === 0 ? false : null
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    if (normalized === 'true' || normalized === 'yes') return true
    if (normalized === 'false' || normalized === 'no') return false
  }
  return null
}

function toTime(value: unknown): number | null {
  if (value instanceof Date) return value.getTime()
  if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value).getTime()
    return Number.isNaN(parsed) ? null : parsed
  }
  return null
}

export function filterMatches<RowT>(row: RowT, filter: CollectionFilterDef<RowT>, value: CollectionFilterValue | undefined): boolean {
  if (isEmptyFilterValue(value)) return true
  const rowValue = filter.getValue ? filter.getValue(row) : readField(row, filter.id)
  const operatorValue = isOperatorValue(value) ? value : null
  const op = operatorValue?.op
  const rawValue = operatorValue ? operatorValue.value : value
  if (op === 'empty') return !hasPresentValue(rowValue)
  if (op === 'notEmpty') return hasPresentValue(rowValue)
  if (filter.type === 'text') {
    const haystack = toText(rowValue).toLowerCase()
    const needle = String(rawValue ?? '').trim().toLowerCase()
    if (op === 'equals') return haystack === needle
    if (op === 'startsWith') return haystack.startsWith(needle)
    if (op === 'notContains') return !haystack.includes(needle)
    return haystack.includes(needle)
  }
  if (filter.type === 'select') {
    const selected = Array.isArray(rawValue) ? rawValue : rawValue === undefined ? [] : [String(rawValue)]
    // Array-valued rows (e.g. per-file fields like courseType/language on a
    // resource with files[]) match when ANY entry matches — same semantics as
    // multiSelect's array branch. Previously an array rowValue was string-joined
    // and silently mismatched every row.
    const matches = Array.isArray(rowValue)
      ? rowValue.some((item) => selected.includes(toText(item)))
      : selected.includes(toText(rowValue))
    return op === 'noneOf' ? !matches : matches
  }
  if (filter.type === 'multiSelect') {
    const selected = Array.isArray(rawValue) ? rawValue : rawValue === undefined ? [] : [String(rawValue)]
    const matches = Array.isArray(rowValue)
      ? rowValue.some((item) => selected.includes(toText(item)))
      : selected.includes(toText(rowValue))
    return op === 'noneOf' ? !matches : matches
  }
  if (filter.type === 'dateRange') {
    const range = isRecord(rawValue) ? rawValue : {}
    const time = toTime(rowValue)
    if (time === null) return false
    const from = typeof range.from === 'string' && range.from ? toTime(range.from) : null
    const to = typeof range.to === 'string' && range.to ? toTime(range.to) : null
    return (from === null || time >= from) && (to === null || time <= to)
  }
  if (filter.type === 'numberRange') {
    const range = isRecord(rawValue) ? rawValue : {}
    const number = toNumber(rowValue)
    if (number === null) return false
    const min = toNumber(range.min)
    const max = toNumber(range.max)
    return (min === null || number >= min) && (max === null || number <= max)
  }
  return toBoolean(rowValue) === value
}

export function compareValues(a: unknown, b: unknown): number {
  const an = toNumber(a)
  const bn = toNumber(b)
  if (an !== null && bn !== null) return an - bn
  const at = toTime(a)
  const bt = toTime(b)
  if (at !== null && bt !== null) return at - bt
  return toText(a).localeCompare(toText(b), undefined, { numeric: true, sensitivity: 'base' })
}

export function mergeFilterDefs<RowT>(columns: CollectionColumnDef<RowT>[], filters: CollectionFilterDef<RowT>[] = []): CollectionFilterDef<RowT>[] {
  const seen = new Set<string>()
  const merged: CollectionFilterDef<RowT>[] = []
  for (const filter of [...filters, ...columns.map((column) => column.filter).filter((filter): filter is CollectionFilterDef<RowT> => Boolean(filter))]) {
    if (seen.has(filter.id)) continue
    seen.add(filter.id)
    merged.push(filter)
  }
  return merged
}

export function readUrlState(options: CollectionUrlStateOptions = {}): Partial<Pick<CollectionState, 'q' | 'page' | 'pageSize' | 'sort' | 'sortStack' | 'filters'>> {
  const search = options.search ?? (typeof window === 'undefined' ? '' : window.location.search)
  const params = new URLSearchParams(stripSearchPrefix(search))
  const key = (name: string) => paramKey(options.urlKey, name)
  // Only keys actually present in the URL — absent keys must not clobber a
  // caller's initialState (deep links) in the `{...initialState, ...readUrlState()}` merge.
  const out: Partial<Pick<CollectionState, 'q' | 'page' | 'pageSize' | 'sort' | 'sortStack' | 'filters'>> = {}
  if (params.has(key('q'))) out.q = params.get(key('q')) ?? undefined
  if (params.has(key('page'))) out.page = Math.max(1, Number(params.get(key('page'))) || 1)
  if (params.has(key('pageSize'))) {
    const pageSize = Math.max(1, Number(params.get(key('pageSize'))) || 0)
    if (pageSize) out.pageSize = pageSize
  }
  if (params.has(key(SORT_QUERY_KEY))) {
    const sortStack = parseSortStack(params.get(key(SORT_QUERY_KEY)))
    out.sort = sortStack[0] ?? null
    if (sortStack.length > 1) out.sortStack = sortStack
  }
  if (options.urlFilterFormat === 'flat') {
    const filters = readFlatFilters(params, options.urlKey)
    if (Object.keys(filters).length) out.filters = filters
  } else if (params.has(key(FILTER_QUERY_KEY))) {
    out.filters = parseFilters(params.get(key(FILTER_QUERY_KEY)))
  }
  return out
}

export function stateToSearch(state: CollectionState, options: CollectionSearchOptions = {}): string {
  const search = options.search ?? (typeof window === 'undefined' ? '' : window.location.search)
  const params = new URLSearchParams(stripSearchPrefix(search))
  const key = (name: string) => paramKey(options.urlKey, name)
  if (state.q.trim()) {
    params.set(key('q'), state.q)
  } else {
    params.delete(key('q'))
  }
  if (state.page > 1) {
    params.set(key('page'), String(state.page))
  } else {
    params.delete(key('page'))
  }
  if (state.pageSize !== (options.defaultPageSize ?? 25)) {
    params.set(key('pageSize'), String(state.pageSize))
  } else {
    params.delete(key('pageSize'))
  }
  const sortStack = normalizeSortStack(state.sortStack?.length ? state.sortStack : (state.sort ? [state.sort] : []))
  if (sortStack.length) {
    params.set(key(SORT_QUERY_KEY), sortStack.map((sort) => `${sort.id}:${sort.dir}`).join(','))
  } else {
    params.delete(key(SORT_QUERY_KEY))
  }
  const activeFilters = Object.fromEntries(Object.entries(state.filters).filter(([, value]) => !isEmptyFilterValue(value)))
  if (options.urlFilterFormat === 'flat') {
    params.delete(key(FILTER_QUERY_KEY))
    writeFlatFilters(params, activeFilters, options.urlKey)
  } else if (Object.keys(activeFilters).length) {
    deleteFlatFilterParams(params, options.urlKey)
    params.set(key(FILTER_QUERY_KEY), JSON.stringify(activeFilters))
  } else {
    params.delete(key(FILTER_QUERY_KEY))
    deleteFlatFilterParams(params, options.urlKey)
  }
  if (options.presetId) {
    params.set(key(PRESET_QUERY_KEY), options.presetId)
  } else {
    params.delete(key(PRESET_QUERY_KEY))
  }
  const query = params.toString()
  return query ? `?${query}` : ''
}

export function readUrlPreset(search: string, urlKey = ''): string | null {
  const params = new URLSearchParams(stripSearchPrefix(search))
  return params.get(paramKey(urlKey, PRESET_QUERY_KEY))
}

export function hasCollectionUrlState(search: string, options: CollectionUrlStateOptions = {}): boolean {
  const params = new URLSearchParams(stripSearchPrefix(search))
  const names = ['q', 'page', 'pageSize', SORT_QUERY_KEY, FILTER_QUERY_KEY, PRESET_QUERY_KEY].map((name) => paramKey(options.urlKey, name))
  if (names.some((name) => params.has(name))) return true
  const flatPrefix = `${paramKey(options.urlKey, 'f')}.`
  return [...params.keys()].some((name) => name.startsWith(flatPrefix))
}

export function storageKey(namespace: string): string {
  return `vjal:collection:${namespace}:views`
}

export function loadSavedViews(namespace?: string): CollectionSavedView[] {
  return loadSavedViewsEnvelope(namespace).views
}

export interface CollectionSavedViewsEnvelope {
  version: 1
  defaultId: string | null
  views: CollectionSavedView[]
}

export function loadSavedViewsEnvelope(namespace?: string): CollectionSavedViewsEnvelope {
  if (!namespace || typeof window === 'undefined') return { version: 1, defaultId: null, views: [] }
  try {
    const parsed: unknown = JSON.parse(window.localStorage.getItem(storageKey(namespace)) ?? '[]')
    if (Array.isArray(parsed)) return { version: 1, defaultId: null, views: parseSavedViews(parsed) }
    if (!isRecord(parsed) || parsed.version !== 1 || !Array.isArray(parsed.views)) return { version: 1, defaultId: null, views: [] }
    return {
      version: 1,
      defaultId: typeof parsed.defaultId === 'string' ? parsed.defaultId : null,
      views: parseSavedViews(parsed.views),
    }
  } catch {
    return { version: 1, defaultId: null, views: [] }
  }
}

export function getCollectionFilterLabel<RowT>(filter: CollectionFilterDef<RowT>, value: CollectionFilterValue): string {
  if (isOperatorValue(value)) return getOperatorFilterLabel(filter, value)
  if (filter.type === 'select') return `${filter.label}: ${filter.options.find((option) => option.value === value)?.label ?? value}`
  if (filter.type === 'multiSelect') {
    const values = Array.isArray(value) ? value : []
    const labels = values.map((item) => filter.options.find((option) => option.value === item)?.label ?? item)
    return `${filter.label}: ${labels.join(', ')}`
  }
  if (filter.type === 'dateRange' && isRecord(value)) return `${filter.label}: ${toText(value.from) || 'Any'} - ${toText(value.to) || 'Any'}`
  if (filter.type === 'numberRange' && isRecord(value)) return `${filter.label}: ${toText(value.min) || 'Any'} - ${toText(value.max) || 'Any'}`
  if (filter.type === 'boolean') return `${filter.label}: ${value ? (filter.trueLabel ?? 'Yes') : (filter.falseLabel ?? 'No')}`
  return `${filter.label}: ${toText(value)}`
}

export function isOperatorValue(value: CollectionFilterValue | undefined): value is CollectionOperatorValue {
  return isRecord(value) && typeof value.op === 'string'
}

export function normalizeSortStack(stack: CollectionSort[]): CollectionSort[] {
  const out: CollectionSort[] = []
  for (const sort of stack) {
    if (!sort.id || (sort.dir !== 'asc' && sort.dir !== 'desc') || out.some((item) => item.id === sort.id)) continue
    out.push(sort)
    if (out.length === 3) break
  }
  return out
}

function parseSortStack(value: string | null): CollectionSort[] {
  if (!value) return []
  return normalizeSortStack(value.split(',').map((part) => {
    const [id, dir] = part.split(':')
    return { id, dir } as CollectionSort
  }))
}

function parseFilters(value: string | null): CollectionFilterState {
  if (!value) return {}
  try {
    const parsed: unknown = JSON.parse(value)
    return isRecord(parsed) ? parsed as CollectionFilterState : {}
  } catch {
    return {}
  }
}

function parseSavedViews(value: unknown[]): CollectionSavedView[] {
  return value.filter((view): view is CollectionSavedView => isRecord(view) && typeof view.id === 'string' && typeof view.name === 'string' && isRecord(view.state))
}

function getOperatorFilterLabel<RowT>(filter: CollectionFilterDef<RowT>, value: CollectionOperatorValue): string {
  if (value.op === 'empty') return `${filter.label} is empty`
  if (value.op === 'notEmpty') return `${filter.label} is not empty`
  const renderedValue = renderFilterValue(filter, value.value)
  const label = operatorLabel(value.op)
  return renderedValue ? `${filter.label} ${label} ${renderedValue}` : `${filter.label} ${label}`
}

function operatorLabel(op: CollectionOperatorValue['op']): string {
  if (op === 'equals' || op === 'anyOf') return 'is'
  if (op === 'startsWith') return 'starts with'
  if (op === 'notContains') return 'does not contain'
  if (op === 'noneOf') return 'is not'
  return 'contains'
}

function renderFilterValue<RowT>(filter: CollectionFilterDef<RowT>, value: string | string[] | undefined): string {
  const values = Array.isArray(value) ? value : value === undefined ? [] : [value]
  if (filter.type === 'select' || filter.type === 'multiSelect') {
    return values.map((item) => filter.options.find((option) => option.value === item)?.label ?? item).join(', ')
  }
  return values.join(', ')
}

function hasPresentValue(value: unknown): boolean {
  if (value === undefined || value === null) return false
  if (typeof value === 'string') return value.trim() !== ''
  if (Array.isArray(value)) return value.length > 0
  return true
}

function stripSearchPrefix(search: string): string {
  return search.startsWith('?') ? search.slice(1) : search
}

function paramKey(urlKey = '', name: string): string {
  return urlKey ? `${urlKey}.${name}` : name
}

function writeFlatFilters(params: URLSearchParams, filters: CollectionFilterState, urlKey = '') {
  deleteFlatFilterParams(params, urlKey)
  for (const [id, raw] of Object.entries(filters)) {
    if (isEmptyFilterValue(raw)) continue
    const key = (suffix = '') => paramKey(urlKey, `f.${id}${suffix}`)
    if (isOperatorValue(raw)) {
      params.set(key('.op'), raw.op)
      if (raw.value !== undefined) params.set(key(), Array.isArray(raw.value) ? raw.value.join(',') : raw.value)
      continue
    }
    if (Array.isArray(raw)) {
      params.set(key(), raw.join(','))
    } else if (isDateRangeValue(raw)) {
      if (raw.from) params.set(key('.from'), raw.from)
      if (raw.to) params.set(key('.to'), raw.to)
    } else if (isNumberRangeValue(raw)) {
      if (raw.min !== undefined) params.set(key('.min'), String(raw.min))
      if (raw.max !== undefined) params.set(key('.max'), String(raw.max))
    } else if (raw !== null && raw !== undefined) {
      params.set(key(), String(raw))
    }
  }
}

function readFlatFilters(params: URLSearchParams, urlKey = ''): CollectionFilterState {
  const prefix = `${paramKey(urlKey, 'f')}.`
  const filters: CollectionFilterState = {}
  const operatorOps: Record<string, string> = {}
  const operatorValues: Record<string, string | string[] | undefined> = {}
  for (const [name, rawValue] of params.entries()) {
    if (!name.startsWith(prefix)) continue
    const suffix = name.slice(prefix.length)
    const [id, part] = suffix.split('.', 2)
    if (!id) continue
    if (part === 'op') {
      operatorOps[id] = rawValue
      continue
    }
    if (part === 'from' || part === 'to') {
      const current = isRecord(filters[id]) ? filters[id] as Record<string, unknown> : {}
      filters[id] = { ...current, [part]: rawValue }
      continue
    }
    if (part === 'min' || part === 'max') {
      const current = isRecord(filters[id]) ? filters[id] as Record<string, unknown> : {}
      const parsed = Number(rawValue)
      filters[id] = { ...current, [part]: Number.isFinite(parsed) ? parsed : undefined }
      continue
    }
    const parsed = parseFlatScalar(rawValue)
    operatorValues[id] = typeof parsed === 'boolean' ? String(parsed) : parsed
    filters[id] = parsed
  }
  for (const [id, op] of Object.entries(operatorOps)) {
    if (isCollectionOperator(op)) filters[id] = { op, value: operatorValues[id] } as CollectionOperatorValue
  }
  return filters
}

function deleteFlatFilterParams(params: URLSearchParams, urlKey = '') {
  const prefix = `${paramKey(urlKey, 'f')}.`
  for (const name of [...params.keys()]) {
    if (name.startsWith(prefix)) params.delete(name)
  }
}

function parseFlatScalar(value: string): string | string[] | boolean {
  if (value.includes(',')) return value.split(',').filter(Boolean)
  if (value === 'true') return true
  if (value === 'false') return false
  return value
}

function isDateRangeValue(value: CollectionFilterValue | undefined): value is { from?: string; to?: string } {
  return isRecord(value) && ('from' in value || 'to' in value)
}

function isNumberRangeValue(value: CollectionFilterValue | undefined): value is { min?: number; max?: number } {
  return isRecord(value) && ('min' in value || 'max' in value)
}

function isCollectionOperator(value: string): value is CollectionOperator {
  return value === 'contains'
    || value === 'equals'
    || value === 'startsWith'
    || value === 'notContains'
    || value === 'anyOf'
    || value === 'noneOf'
    || value === 'empty'
    || value === 'notEmpty'
}
